/**
 * Préparation d'une photo avant envoi dans Firebase Storage : décodage dans le
 * navigateur, redimensionnement et ré-encodage en JPEG.
 *
 * Le ré-encodage garantit que seul un JPEG « propre » part dans Storage
 * (pas de SVG, pas de métadonnées EXIF/GPS du téléphone, poids maîtrisé).
 */
import { MAX_IMAGE_UPLOAD_BYTES } from './restaurantShowcase';
import { UserFacingError } from './userFacingError';

/** Taille maximale du fichier choisi, avant compression (photos de téléphone). */
const MAX_SOURCE_BYTES = 25 * 1024 * 1024;

/** Côté maximal accepté avant décodage (une image géante saturerait la mémoire du navigateur). */
const MAX_SOURCE_DIMENSION = 12_000;

/** Erreur de préparation : message français affichable tel quel. */
export class ImagePreparationError extends UserFacingError {}

const UNREADABLE = 'Impossible de lire cette image. Utilisez une photo JPG, PNG ou WebP.';
const HEIC_UNREADABLE = 'Votre navigateur ne sait pas lire les photos HEIC (iPhone). Enregistrez-la en JPG '
    + '(ou choisissez « Le plus compatible » dans Réglages > Appareil photo > Formats), puis réessayez.';

const isHeic = (file: File) => /heic|heif/i.test(file.type) || /\.(heic|heif)$/i.test(file.name);

/**
 * Lit largeur × hauteur dans l'en-tête PNG, JPEG ou WebP sans décoder l'image.
 * Retourne null si le format n'est pas reconnu (le contrôle a alors lieu après décodage).
 */
async function readImageSize(file: Blob): Promise<{ width: number; height: number } | null> {
    const bytes = new Uint8Array(await file.slice(0, 512 * 1024).arrayBuffer());
    const view = new DataView(bytes.buffer);
    const ascii = (offset: number, length: number) =>
        String.fromCharCode(...bytes.subarray(offset, offset + length));

    // PNG : IHDR juste après la signature.
    if (bytes.length >= 24 && bytes[0] === 0x89 && ascii(1, 3) === 'PNG') {
        return { width: view.getUint32(16), height: view.getUint32(20) };
    }
    // WebP : conteneur RIFF, puis VP8X / VP8 / VP8L.
    if (bytes.length >= 30 && ascii(0, 4) === 'RIFF' && ascii(8, 4) === 'WEBP') {
        const chunk = ascii(12, 4);
        if (chunk === 'VP8X') {
            return {
                width: 1 + (bytes[24] | (bytes[25] << 8) | (bytes[26] << 16)),
                height: 1 + (bytes[27] | (bytes[28] << 8) | (bytes[29] << 16)),
            };
        }
        if (chunk === 'VP8 ') return { width: view.getUint16(26, true) & 0x3fff, height: view.getUint16(28, true) & 0x3fff };
        if (chunk === 'VP8L') {
            const bits = view.getUint32(21, true);
            return { width: 1 + (bits & 0x3fff), height: 1 + ((bits >> 14) & 0x3fff) };
        }
        return null;
    }
    // JPEG : parcours des segments jusqu'au marqueur SOF (début de trame).
    if (bytes.length >= 4 && bytes[0] === 0xff && bytes[1] === 0xd8) {
        let offset = 2;
        while (offset + 9 < bytes.length) {
            if (bytes[offset] !== 0xff) return null;
            const marker = bytes[offset + 1];
            if (marker === 0xff) { offset += 1; continue; } // octet de remplissage
            if (marker === 0xda) return null; // début des données sans SOF : inconnu
            const isSof = marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
            if (isSof) return { width: view.getUint16(offset + 7), height: view.getUint16(offset + 5) };
            offset += 2 + view.getUint16(offset + 2);
        }
    }
    return null;
}

interface DecodedImage {
    source: CanvasImageSource;
    width: number;
    height: number;
    release: () => void;
}

async function decodeImage(file: File): Promise<DecodedImage> {
    if (typeof createImageBitmap === 'function') {
        try {
            // Applique l'orientation EXIF (photo prise téléphone tourné).
            const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
            return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close() };
        } catch {
            // Repli sur <img> (anciens Safari, certains formats).
        }
    }
    const objectUrl = URL.createObjectURL(file);
    try {
        const img = new Image();
        img.decoding = 'async';
        img.src = objectUrl;
        await img.decode();
        return {
            source: img,
            width: img.naturalWidth,
            height: img.naturalHeight,
            release: () => URL.revokeObjectURL(objectUrl),
        };
    } catch {
        URL.revokeObjectURL(objectUrl);
        throw new ImagePreparationError(isHeic(file) ? HEIC_UNREADABLE : UNREADABLE);
    }
}

function canvasToJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
    return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
}

const tooLarge = (size: { width: number; height: number }) =>
    Math.max(size.width, size.height) > MAX_SOURCE_DIMENSION;
const TOO_LARGE = `Cette photo est trop grande (${MAX_SOURCE_DIMENSION.toLocaleString('fr-FR')} pixels de côté maximum). `
    + 'Réduisez-la avant de l\'envoyer.';

/**
 * Retourne un JPEG d'au plus `maxDimension` px sur le plus grand côté et de
 * moins de MAX_IMAGE_UPLOAD_BYTES. Lève ImagePreparationError (message en
 * français, affichable tel quel) si le fichier n'est pas une image exploitable.
 */
export async function prepareImageForUpload(file: File, maxDimension = 1600): Promise<Blob> {
    // file.type est parfois vide (HEIC sur certains navigateurs) : on tente alors le décodage.
    if (file.type && (!file.type.startsWith('image/') || file.type === 'image/svg+xml')) {
        throw new ImagePreparationError('Ce fichier n\'est pas une photo. Formats acceptés : JPG, PNG, WebP.');
    }
    if (file.size > MAX_SOURCE_BYTES) {
        throw new ImagePreparationError('Cette photo est trop lourde (25 Mo maximum).');
    }
    const headerSize = await readImageSize(file).catch(() => null);
    if (headerSize && tooLarge(headerSize)) throw new ImagePreparationError(TOO_LARGE);

    const image = await decodeImage(file);
    try {
        if (!image.width || !image.height) throw new ImagePreparationError(UNREADABLE);
        if (tooLarge(image)) throw new ImagePreparationError(TOO_LARGE);
        const scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.width * scale));
        canvas.height = Math.max(1, Math.round(image.height * scale));
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new ImagePreparationError('Votre navigateur ne permet pas de préparer la photo.');
        // Fond blanc : un PNG transparent ne doit pas devenir noir en JPEG.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(image.source, 0, 0, canvas.width, canvas.height);

        for (const quality of [0.85, 0.7, 0.55]) {
            const blob = await canvasToJpeg(canvas, quality);
            // toBlob retombe sur du PNG si le navigateur ne sait pas encoder en JPEG :
            // Storage n'accepte que image/jpeg, autant le dire tout de suite.
            if (blob && blob.type !== 'image/jpeg') {
                throw new ImagePreparationError('Votre navigateur ne permet pas de préparer la photo. Essayez avec Chrome, Safari ou Firefox à jour.');
            }
            if (blob && blob.size > 0 && blob.size < MAX_IMAGE_UPLOAD_BYTES) return blob;
        }
        throw new ImagePreparationError('Cette photo reste trop lourde après compression (5 Mo maximum).');
    } finally {
        image.release();
    }
}
